import React from 'react';

import { format } from 'date-fns';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { formatTimePeriodToString } from 'config/api/yearPlannerActivities/yearPlannerActivities.parsers';
import { YearPlannerActivity } from 'config/api/yearPlannerActivities/yearPlannerActivities.types';
import { translatedOrFallback } from 'helpers/safeT/safeT';
import assets_messages from 'messages/assets_messages';
import print_template_messages from 'messages/print_template_messages';

import { EMPTY_PRINT_VALUE, renderPrintableValue, truncatePrintableText } from '../../utils/printText';

const PRINT_STYLES = `
.ps-assets-print { color: #111; font-family: Arial, sans-serif; font-size: 12px; line-height: 1.4; padding: 24px; }
.ps-assets-print__header { border-bottom: 1px solid #333; margin-bottom: 18px; padding-bottom: 10px; }
.ps-assets-print__title { font-size: 22px; margin: 0 0 6px; }
.ps-assets-print__meta { color: #555; margin: 0; }
.ps-assets-print__activity { border: 1px solid #d5d5d5; break-inside: avoid; margin-bottom: 12px; padding: 10px; page-break-inside: avoid; }
.ps-assets-print__activity-title { font-size: 15px; margin: 0 0 8px; }
.ps-assets-print__definition { display: grid; gap: 6px 12px; grid-template-columns: 140px 1fr; margin: 0; }
.ps-assets-print__definition dt { font-weight: 700; }
.ps-assets-print__definition dd { margin: 0; white-space: pre-wrap; }
.ps-assets-print__empty { border: 1px solid #d5d5d5; padding: 12px; }
@media print {
  .ps-assets-print { padding: 0; }
  .ps-assets-print__activity { break-inside: avoid; page-break-inside: avoid; }
}
`;

type Props = {
  activities: YearPlannerActivity[];
  printedAt?: Date;
};

const getActivityName = (activity: YearPlannerActivity, t: TFunction): string =>
  renderPrintableValue(
    activity.activity ? translatedOrFallback(t, activity.activity.nameTranslationKey, activity.activity.name) : activity.displayName,
  );

const getActivityDescription = (activity: YearPlannerActivity, t: TFunction): string =>
  activity.activity
    ? translatedOrFallback(t, activity.activity.descriptionTranslationKey, activity.activity.description)
    : activity.displayDescription;

const getCategories = (activity: YearPlannerActivity, t: TFunction): string => {
  const categories = activity.categories
    ?.map(category => translatedOrFallback(t, category.nameTranslationKey, category.name))
    .map(renderPrintableValue)
    .filter(value => value !== EMPTY_PRINT_VALUE);
  return categories?.length ? categories.join(', ') : EMPTY_PRINT_VALUE;
};

const getDate = (activity: YearPlannerActivity): string => {
  if (!activity.begin && !activity.end) return EMPTY_PRINT_VALUE;
  const begin = activity.begin ? formatTimePeriodToString(activity.begin) : EMPTY_PRINT_VALUE;
  const end = activity.end ? formatTimePeriodToString(activity.end) : EMPTY_PRINT_VALUE;
  return `${begin} - ${end}`;
};

const YearPlannerActivitiesPrintTemplate: React.FC<Props> = ({ activities, printedAt = new Date() }) => {
  const { t } = useTranslation();

  return (
    <>
      <style>{PRINT_STYLES}</style>
      <article className='ps-assets-print'>
        <header className='ps-assets-print__header'>
          <h1 className='ps-assets-print__title'>{t(assets_messages.print.year_planner_title)}</h1>
          <p className='ps-assets-print__meta'>
            {t(print_template_messages.footer.printed_on)}: {format(printedAt, 'yyyy-MM-dd HH:mm')}
          </p>
        </header>
        {activities.length ? (
          activities.map(activity => (
            <section key={activity.id} className='ps-assets-print__activity'>
              <h2 className='ps-assets-print__activity-title'>{getActivityName(activity, t)}</h2>
              <dl className='ps-assets-print__definition'>
                <dt>{t(assets_messages.activities_table.date)}</dt>
                <dd>{getDate(activity)}</dd>
                <dt>{t(assets_messages.print.planned_asset_types)}</dt>
                <dd>{getCategories(activity, t)}</dd>
                <dt>{t(assets_messages.activities_table.description)}</dt>
                <dd>{truncatePrintableText(getActivityDescription(activity, t))}</dd>
              </dl>
            </section>
          ))
        ) : (
          <p className='ps-assets-print__empty'>{t(assets_messages.print.empty_year_planner)}</p>
        )}
      </article>
    </>
  );
};

export default YearPlannerActivitiesPrintTemplate;
